import React, { useCallback, useEffect, useState } from 'react'

import { makeStyles } from '@mui/styles'
import { Box, Typography, Theme, alpha } from '@mui/material'
import Facebook from '@mui/icons-material/Facebook'
import Instagram from '@mui/icons-material/Instagram'
import Google from '@mui/icons-material/Google'

import ImageComponent from '@professocial/professocialFormComponents/FormComponents/ImageComponent'
import { CalendarDayObject, dndStateProps } from '../PostCalendar'
import CalendarLightbox from './CalendarLightbox'

import { AboType, GetUrlInformationRequest, NetworkType, PostContent, WebResourceMetaData } from '@3m5/sm_publish-frontend-api'
import { useDispatch, useSelector } from 'react-redux'
import { getUser, namedRequestData } from '../../../../../store/common/common.selectors'
import { useHistory, useLocation } from 'react-router-dom'
import { DateObject, PostObject } from '../ProfessocialCalendar'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import VideoComponent from '@professocial/professocialFormComponents/FormComponents/VideoComponent'
import { ActionCall } from '@professocial/professocialTypes'
import { CoreRootState } from '../../../../../store/coreRoot.reducer'
import { getUrlInformation } from '../../../../../store/dashboard/dashboard.selector'
import { getUrlInformationInitiated } from '../../../../../store/dashboard/dashboard.actions'

interface IProps {
	post: PostContent;
	postMetaData?: PostObject;
	id?: string;
	day: CalendarDayObject;
	published: boolean;
	networks: NetworkType[];
	position: number;
	dragAndDrop: dndStateProps;
	editSettings?: (topicId: string | undefined, aboType: AboType) => void;
	calendarData?: DateObject;
	setSelectedDate?: (selectedDate: number) => void;
	onHandleDnD: (data: dndStateProps) => void;
}

const PostCard: React.FC<IProps> = (props) => {
  const location = useLocation<{ detail: any }>()
  const history = useHistory()
  const user = useSelector(getUser)
  const dispatch = useDispatch()

  const [post, setPost] = useState<PostContent>(props.post)
  const [postMetaData, setPostMetaData] = useState<PostObject | undefined>(props.postMetaData)

  const [isOpen, setIsOpen] = useState(false)
  const notPublished = !props.published

  const dispatchGetUrlInformation = useCallback((params: { data: GetUrlInformationRequest, id: string }) => dispatch(getUrlInformationInitiated(params)), [dispatch])

  const statusOnUrlInformationLoad: ActionCall | undefined = useSelector((state: CoreRootState) => namedRequestData(state, getUrlInformationInitiated.type))
  const urlInformation: WebResourceMetaData | undefined = useSelector(getUrlInformation)

  const getColor = () => {
    let color = '#E99425'
    if (post.image?.url || post.video?.url) color = '#258EE9'
    if (post.link?.url) color = '#E92525'
    return color
  }

  const useStyles = makeStyles((theme: Theme) => ({
    postCard: {
      display: props.dragAndDrop.draggedTo === props.position && props.id === props.dragAndDrop.draggedItemData?.id ? 'none' : 'flex',
      width: '100%',
      borderColor: getColor(),
      borderTopWidth: '3px',
      borderTopStyle: 'solid',
      backgroundColor: '#F2F2F2',
      padding: theme.spacing(1),
      opacity: notPublished ? 1 : 0.5,
      transition: 'all 300ms ease-in-out',
      marginBottom: theme.spacing(0.8),
      '&:hover': {
        cursor: notPublished && user?.role !== 'USER' ? 'pointer' : '',
        opacity: 1,
      },
      '&:active': {
        cursor: notPublished && user?.role !== 'USER' ? 'grabbing' : '',
      },
      '&:last-child': {
        marginBottom: 0,
      },
    },
    postCardContent: {
      display: 'block',
    },
    ownPosting: {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
    },
    postCardContentImg: {
      maxHeight: theme.spacing(10),
      marginTop: theme.spacing(1),
      maxWidth: '100%',
    },
    postCardContentVideo: {
      position: 'relative',
      objectFit: 'contain',
      maxHeight: theme.spacing(10),
      maxWidth: theme.spacing(10),
      marginTop: theme.spacing(1),
      zIndex: 0,
    },
    postCardIcons: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: 'auto',
    },
    scaled: {
      transform: 'scale(1.03)',
    },
    pulseOut: {
      animation: '$pulse .3s ease-in-out',
    },
    '@keyframes pulse': {
      from: {
        transform: 'scale3d(1.03, 1.03, 1.03)',
      },
      '50%': {
        transform: 'scale3d(.95, .95, .95)',
      },
      to: {
        transform: 'scale3d(1, 1., 1)',
      },
    },
  }))
  const classes = useStyles()

  const onDragStart = (event: React.MouseEvent<HTMLDivElement>) => {
    const id = event.currentTarget.dataset.id
    const day = event.currentTarget.dataset.day

    if (notPublished && id && day) {
      const itemDragged = props.calendarData?.[day][id]
      props.onHandleDnD({
        ...props.dragAndDrop,
        isDragging: true,
        draggedItemData: { id, day, postStart: itemDragged?.template?.postStart },
      })
    }
  }

  const onDragEnd = (event: React.MouseEvent<HTMLDivElement>) => {
    if (notPublished) {
      props.onHandleDnD({
        ...props.dragAndDrop,
        isDragging: false,
      })
    }
  }

  const onScale = props.id === props.dragAndDrop.draggedItemData?.id && props.dragAndDrop.isDragging && notPublished ? classes.scaled : ''
  const onScaleOut = props.id === props.dragAndDrop.draggedItemData?.id && !props.dragAndDrop.isDragging && notPublished ? classes.pulseOut : ''

  useEffect(() => {
    if (location?.state?.detail && props.id === location.state.detail.templateId) {
      setIsOpen(true)
      history.replace({ state: {} })
    }
  }, [])

  useEffect(() => {
    setPost(props.post)
    setPostMetaData(props.postMetaData)
  }, [props])

  useEffect(() => {
    // fetch link data
    if (post.link?.url && !post.link?.imageUrl && props.id) {
      // use id to differentiate upcoming data
      dispatchGetUrlInformation({ data: { url: post.link.url }, id: props.id })
    }
  }, [post])

  useEffect(() => {
    if (post?.link?.url && urlInformation?.url && statusOnUrlInformationLoad?.status === 'success' && statusOnUrlInformationLoad?.id === props.id) {
      setPost({ ...post, link: {
        url: urlInformation.url,
        caption: urlInformation.title,
        description: urlInformation.description,
        imageUrl: urlInformation.metaImage?.url,
      } })
    }
  }, [urlInformation, statusOnUrlInformationLoad])

  return (
    <>
      <Box
        className={`${classes.postCard} ${onScale} ${onScaleOut} ${!postMetaData?.template && classes.ownPosting}`}
        component="div"
        key={props.id}
        data-id={props.id}
        data-day={formatDate(new Date(props.day.date).getTime(), 'dd.MM.yyyy')}
        draggable={notPublished}
        onDragStart={user?.role === 'USER' ? undefined : onDragStart}
        onDragEnd={user?.role === 'USER' ? undefined : onDragEnd}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Box className={classes.postCardContent}>
          {post.text && <Typography
            variant="body2">{post.text.substring(0, 25)}{post.text.length > 24 && '...'}</Typography>}
          {post.image && <ImageComponent draggable={false} className={`${classes.postCardContentImg}`}
            src={post.image.url}
            fallback="/src/shared/assets/images/icon-img-placeholder.svg"/>}
          {post.video && <VideoComponent
            className={classes.postCardContentVideo}
            fallbackThumb={'/src/shared/assets/images/icon-img-placeholder.svg'}
            src={post.video.url}
          />}
          {post.link && <ImageComponent draggable={false} className={`${classes.postCardContentImg}`}
            src={post.link.imageUrl}
            fallback="/src/shared/assets/images/icon-img-placeholder.svg"/>}
        </Box>
        <Box className={classes.postCardIcons} component="div">
          {props.networks.find((elem) => elem === NetworkType.Facebook) &&
						<Facebook style={{ fontSize: '1rem' }}/>}
          {props.networks.find((elem) => elem === NetworkType.Instagram) &&
						<Instagram style={{ fontSize: '1rem' }}/>}
          {props.networks.find((elem) => elem === NetworkType.GoogleMyBusiness) &&
						<Google style={{ fontSize: '1rem' }}/>}
        </Box>
      </Box>
      {isOpen && (
        <CalendarLightbox
          isOpen={isOpen}
          post={post}
          postMetaData={postMetaData}
          published={props.published}
          setIsOpen={setIsOpen}
        />
      )}
    </>
  )
}

export default PostCard
